// Moment store — the "keep tonight" moment write path (data layer).
//
// A moment is a hidden chat_messages row (NON_DISPLAY) carrying a photo (and, in
// the later increment, a short line) keyed to the shared per-night key.
// persistMessage is the DB-first delivery + recovery: the author writes one row;
// the peer reads it on their next Detail mount via loadMoments. Live push to an
// already-open shelf is deferred (ConnectionStateContext drops the unknown
// 'moment' insert); pull-on-mount covers v1.
//
// Permanent data layer (CLAUDE.md § The Data Layer Is Permanent): no React, no
// DOM — carries to the native app.

import { persistMessage } from '../chat/messageStore';
import { buildMomentMessage } from '../chat/messageBuilders';
import { nightKey } from './nightKey';
import { getSenderId, getProfileUserId } from '../../utils/identityUtils';

/**
 * Write a moment to a connection. `fields` pass straight to buildMomentMessage:
 * momentType ('photo'|'text'), nightKey, photoUrl, text, sender ('profile'|'anon'),
 * senderId, senderProfileUserId.
 * @returns {Promise<{ok: boolean, message: object}>}
 */
export async function sendMoment(chatRoomId, fields) {
  const message = buildMomentMessage(fields);
  const ok = await persistMessage(chatRoomId, message);
  return { ok, message };
}

/**
 * The exchange photo becoming the meeting's first photo-moment — one home for
 * the host photo-moment write shape (the night key + the fixed profile-author
 * payload), called from both in-exchange capture sites (the THREAD chat chip
 * and the host-flow camera). Returns { ok }; the caller logs the analytics.
 *
 * @param {string} chatRoomId
 * @param {object} metadata  the meeting's metadata (meetingDate + meetingUtcOffset)
 * @param {string} photoUrl  the already-uploaded chat-images URL (reused, not re-uploaded)
 */
export async function sendPhotoMoment(chatRoomId, metadata, photoUrl) {
  return sendMoment(chatRoomId, {
    momentType: 'photo',
    nightKey: nightKey(metadata?.meetingDate, metadata?.meetingUtcOffset),
    // The meeting this moment belongs to (Detail attaches by night today; by key with F)
    ...(metadata?.meetingKey ? { meetingKey: metadata.meetingKey } : {}),
    photoUrl,
    sender: 'profile',
    senderId: getSenderId(),
    senderProfileUserId: getProfileUserId(),
  });
}
